/**
 * Memory viewer (ADR-0010) — durable curated facts scoped to tenant / user / case
 * that the AI Gateway injects into prompts. Only approved facts are injected.
 * Admin/All-scoped (child of the Intelligence nav item).
 */
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { useShellSlot } from '../components/shellContext';
import { type MemoryItem, useMemoryItems } from '../hooks/useAgentPlatform';

export function MemoryPage() {
  useShellSlot({ breadcrumb: 'Memory' });
  const { items, available, isLoading, refresh } = useMemoryItems();

  const cols: Column<MemoryItem>[] = [
    { key: 'scope', header: 'Scope', render: (m) => <StatusBadge value={m.scope} /> },
    { key: 'subject', header: 'Subject', render: (m) => m.subject_id },
    { key: 'key', header: 'Key', render: (m) => <strong>{m.key}</strong> },
    { key: 'value', header: 'Value', render: (m) => m.value },
    {
      key: 'approved',
      header: 'Injected?',
      render: (m) => (
        <StatusBadge
          status={m.approved ? 'success' : 'warning'}
          label={m.approved ? 'approved' : 'pending'}
        />
      ),
    },
    { key: 'source', header: 'Source', render: (m) => m.source },
  ];

  return (
    <>
      <PageHeader
        title="Memory"
        subtitle="Curated facts injected into AI prompts. Only approved facts are used; AI-derived facts await sign-off."
        actions={
          <button className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      {!available && (
        <p role="status" style={{ color: '#8a6d3b', fontSize: '0.86rem', marginBottom: 12 }}>
          Memory service unavailable — showing an empty state.
        </p>
      )}

      <Card title="Curated facts" padded={false}>
        {isLoading && !items.length ? (
          <div style={{ padding: 24 }}>
            <LoadingIndicator text="Loading memory…" />
          </div>
        ) : (
          <DataTable
            columns={cols}
            rows={items}
            rowKey={(m) => m.id}
            emptyTitle="No curated facts"
            emptyDescription="Curate tenant / user / case facts to personalize advisory AI."
          />
        )}
      </Card>
    </>
  );
}
